import React, { memo, useMemo, useState } from 'react';

import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import { taskApi } from 'app/services/api';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { STATUS_TABLE_HIDDEN_ENTRY_KINDS } from 'constants/tasks';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useSort from 'hooks/useSort';
import { getTaskStatusLabel } from 'utils/tasks';

import { TasksTestIds } from '../Tasks.testIds';

import { taskHistoryTableConfig } from './TaskHistoryTable.config';
import { eventDateKey, TASK_HISTORY_SKELETON_ROWS } from './TaskHistoryTable.constants';

const TaskHistoryTable = ({ taskId, setIsLoading, isLoading }) => {
  const { order, onRequestSort } = useSort();

  const [taskHistory, setTaskHistory] = useState([]);

  const orderedTaskHistory = useMemo(
    () =>
      _.orderBy(
        taskHistory,
        [
          historyItem =>
            order.id === eventDateKey
              ? new Date(historyItem[order.id])
              : historyItem[order.id]?.toLowerCase(),
        ],
        [order.direction],
      ).map(({ status, ...historyItem }, index) => ({
        ...historyItem,
        id: historyItem.taskHistoryRecordId ?? `${historyItem[eventDateKey]}-${status}-${index}`,
        status: getTaskStatusLabel(status),
      })),
    [taskHistory, order],
  );

  useEffectSafeStrictMode(() => {
    setIsLoading(true);
    taskApi
      .getTaskHistory({ taskId })
      // Thread messages live in TaskThread (Task Info -> Activity); the status table ignores them.
      .then(({ data }) =>
        setTaskHistory(
          (data || []).filter(
            ({ entryKind }) => !STATUS_TABLE_HIDDEN_ENTRY_KINDS.includes(entryKind),
          ),
        ),
      )
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [setIsLoading, taskId]);

  return (
    <TableCDS
      isStickyHeader
      config={taskHistoryTableConfig}
      data={orderedTaskHistory}
      isLoading={isLoading}
      order={order}
      rowTestId={TasksTestIds.getTaskHistoryRowId(taskId)}
      skeletonRows={TASK_HISTORY_SKELETON_ROWS}
      onRequestSort={onRequestSort}
    />
  );
};

TaskHistoryTable.propTypes = {
  ...WithLoadingPropTypes,
  taskId: PropTypes.string.isRequired,
};

export default compose(withLoading, memo)(TaskHistoryTable);
